import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import MDButton from "components/MDButton";
import DebitOrderSignaturePad from "layouts/customer-view/components/DebitOrderSignaturePad";
import { api } from "utils/api";
import { toHtmlDateInputValue } from "utils/dateFormatter";

const EMPTY_FORM = {
  ref: "",
  name: "",
  id: "",
  contact: "",
  address: "",
  bank_name: "",
  branch: "",
  branch_code: "",
  account: "",
  type: "",
  day: "",
  state: "",
  frequency: "",
  recurring_amount: "",
  first_amount: "",
  payments: "",
  first_payment_date: "",
  next_payment_date: "",
  last_payment_date: "",
  start_date: "",
  expiry_date: "",
};

function debitOrderToForm(debitOrder) {
  if (!debitOrder) {
    return { ...EMPTY_FORM };
  }
  return {
    ref: debitOrder.ref ?? "",
    name: debitOrder.name ?? "",
    id: debitOrder.id ?? "",
    contact: debitOrder.contact ?? "",
    address: debitOrder.address ?? "",
    bank_name: debitOrder.bank_name ?? "",
    branch: debitOrder.branch ?? "",
    branch_code: debitOrder.branch_code ?? "",
    account: debitOrder.account ?? "",
    type: debitOrder.type ?? "",
    day: debitOrder.day != null ? String(debitOrder.day) : "",
    state: debitOrder.state ?? "",
    frequency: debitOrder.frequency != null ? String(debitOrder.frequency) : "",
    recurring_amount:
      debitOrder.recurring_amount != null ? String(debitOrder.recurring_amount) : "",
    first_amount: debitOrder.first_amount != null ? String(debitOrder.first_amount) : "",
    payments: debitOrder.payments != null ? String(debitOrder.payments) : "",
    first_payment_date: toHtmlDateInputValue(debitOrder.first_payment_date) || "",
    next_payment_date: toHtmlDateInputValue(debitOrder.next_payment_date) || "",
    last_payment_date: toHtmlDateInputValue(debitOrder.last_payment_date) || "",
    start_date: toHtmlDateInputValue(debitOrder.start_date) || "",
    expiry_date: toHtmlDateInputValue(debitOrder.expiry_date) || "",
  };
}

function formToPayload(form, signature) {
  const payload = { ...form, signature: signature || null };
  ["day", "frequency", "payments"].forEach((key) => {
    if (payload[key] === "") {
      payload[key] = null;
    } else if (payload[key] != null) {
      payload[key] = Number(payload[key]);
    }
  });
  ["recurring_amount", "first_amount"].forEach((key) => {
    if (payload[key] === "") {
      payload[key] = null;
    } else if (payload[key] != null) {
      payload[key] = Number(payload[key]);
    }
  });
  Object.keys(payload).forEach((key) => {
    if (payload[key] === "") {
      payload[key] = null;
    }
  });
  return payload;
}

function getApiErrorMessage(err) {
  return (
    err?.responseData?.error ||
    err?.technicalMessage ||
    err?.message ||
    "Request failed"
  );
}

function CustomerDebitOrderEditDialog({
  open,
  onClose,
  customerId,
  debitOrder,
  onSaved,
}) {
  const isEdit = Boolean(debitOrder?.customer_debit_order_id);
  const [form, setForm] = useState(EMPTY_FORM);
  const [signature, setSignature] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    setForm(debitOrderToForm(debitOrder));
    setSignature(debitOrder?.signature || null);
    setError(null);
  }, [open, debitOrder]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = formToPayload(form, signature);
      if (isEdit) {
        const response = await api.put(
          `/db/customers/${customerId}/debit-order/${debitOrder.customer_debit_order_id}`,
          payload
        );
        onSaved(response?.data ?? null);
      } else {
        const response = await api.post(`/db/customers/${customerId}/debit-order`, payload);
        onSaved(response?.data ?? null);
      }
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const fieldDefs = useMemo(
    () => [
      { key: "ref", label: "DOS reference", required: false, xs: 12, sm: 6 },
      { key: "name", label: "Account holder", required: true, xs: 12, sm: 6 },
      { key: "id", label: "ID number", required: true, xs: 12, sm: 6 },
      { key: "contact", label: "Contact", required: true, xs: 12, sm: 6 },
      { key: "address", label: "Address", required: true, xs: 12 },
      { key: "bank_name", label: "Bank", required: false, xs: 12, sm: 6 },
      { key: "branch", label: "Branch", required: false, xs: 12, sm: 6 },
      { key: "branch_code", label: "Branch code", required: true, xs: 12, sm: 6 },
      { key: "account", label: "Account number", required: true, xs: 12, sm: 6 },
      { key: "type", label: "Account type", required: false, xs: 12, sm: 6 },
      { key: "day", label: "Debit day (1–31)", required: true, xs: 12, sm: 6, type: "number" },
      { key: "state", label: "State", required: false, xs: 12, sm: 6 },
      { key: "frequency", label: "Frequency", required: false, xs: 12, sm: 6, type: "number" },
      { key: "recurring_amount", label: "Recurring amount", required: false, xs: 12, sm: 6, type: "number" },
      { key: "first_amount", label: "First amount", required: false, xs: 12, sm: 6, type: "number" },
      { key: "payments", label: "Payments", required: false, xs: 12, sm: 6, type: "number" },
      { key: "first_payment_date", label: "First payment", required: false, xs: 12, sm: 6, type: "date" },
      { key: "next_payment_date", label: "Next payment", required: false, xs: 12, sm: 6, type: "date" },
      { key: "last_payment_date", label: "Last payment", required: false, xs: 12, sm: 6, type: "date" },
      { key: "start_date", label: "Start date", required: false, xs: 12, sm: 6, type: "date" },
      { key: "expiry_date", label: "Expiry date", required: false, xs: 12, sm: 6, type: "date" },
    ],
    []
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle>{isEdit ? "Edit debit order" : "Add debit order"}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {fieldDefs.map(({ key, label, required, xs, sm, type, multiline, rows }) => (
            <Grid item xs={xs} sm={sm ?? xs} key={key}>
              <TextField
                fullWidth
                size="small"
                label={label}
                name={key}
                type={type || "text"}
                required={required}
                value={form[key]}
                onChange={handleChange(key)}
                multiline={multiline}
                rows={rows}
                InputLabelProps={type === "date" ? { shrink: true } : undefined}
              />
            </Grid>
          ))}
          <Grid item xs={12}>
            <DebitOrderSignaturePad
              value={signature}
              onChange={setSignature}
              disabled={saving}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton variant="outlined" color="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </MDButton>
        <MDButton variant="gradient" color="info" onClick={handleSubmit} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

CustomerDebitOrderEditDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  debitOrder: PropTypes.object,
  onSaved: PropTypes.func.isRequired,
};

export default CustomerDebitOrderEditDialog;
